import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import getClassMetadata from "../../Common/classMetadataService";
import LoadingContentWrapper from "../../Common/Loading/LoadingContentWrapper";
import FormButtons from "../../Common/EditForm/formButtons";
import DecimalInputFormRow from "../../Common/EditForm/DecimalInput/DecimalInputReactNumberFormatFormRow";
import getDecimalMetadata from "../../Common/EditForm/ClassMetadata/getDecimalMetadata";
import TextAreaSection from "../../Common/EditForm/TextArea/TextAreaSection";
import getTextAreaMetadata from "../../Common/EditForm/ClassMetadata/getTextAreaMetadata";
import businessDocumentType from "../../BusinessDocuments/businessDocumentType";
import type businessDocumentItemVM from "../../BusinessDocuments/BusinessDocumentItemSelector/businessDocumentItemVM";
import type { ProductReturnRequestItemViewModel } from "./Vm/ProductReturnRequestItemViewModel";
import { v4 as uuidv4 } from "uuid";
import BusinessDocumentItemSelectorV2Fragment, {
  type BusinessDocumentItemSelectorFragmentV2Handle,
} from "../../BusinessDocuments/BusinessDocumentItemSelector/BusinessDocumentItemSelectorFragmentV2";

export default function ProductReturnRequestItemForm(props: {
  values: ProductReturnRequestItemViewModel | null;
  onOk: (values: ProductReturnRequestItemViewModel) => void;
  onCancel: () => void;
  businessDocumentType: number;
  documentNumber: string;
}) {
  const vmDefaultValues: ProductReturnRequestItemViewModel = {
    id: uuidv4(),
    documentItem: null,
    amount: null,
    comments: "",
  };

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isEditForm: boolean = !!props.values; // helper to determine if this is a new or edit form
  // biome-ignore lint/suspicious/noExplicitAny: medata is server-dependent and can contain various properties, so it's not feasible to strongly type it
  const [metadata, setMetadata] = useState<{ [key: string]: any }>({});

  const methods = useForm<ProductReturnRequestItemViewModel>({
    mode: "onTouched",
    defaultValues: vmDefaultValues,
  });

  const { formState, control, getValues, reset, handleSubmit, watch, trigger } =
    methods;

  const { isSubmitting } = formState;

  const onSubmit = async (values: ProductReturnRequestItemViewModel) => {
    // let vmToSave = Object.assign({}, viewModel, values); // if values contain all the necessary data, this step will be unnecessary
    props.onOk(values);
    return;
  };

  const onInitialize = async () => {
    const metadata_response = await getClassMetadata("ComplaintItemVM");
    setMetadata(metadata_response);

    if (isEditForm) {
      // biome-ignore lint/style/noNonNullAssertion: when isEditForm is true, props.values cannot be null
      reset(props.values!);
    } else {
      reset(vmDefaultValues);
    }

    setIsLoading(false);
  };

  const businessDocumentItemSelectorRef =
    useRef<BusinessDocumentItemSelectorFragmentV2Handle>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: because onInitialize is only meant to be called once on component mount, and it does not have any dependencies
  useEffect(() => {
    onInitialize();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch is a function that returns the current form values and is not expected to change, and trigger is also a stable function from react-hook-form, so it's safe to omit them from the dependency array
  useEffect(() => {
    const subscription = watch(() => {
      trigger("amount", { shouldFocus: true });
    });
    const unsubscribe = () => subscription.unsubscribe();
    return unsubscribe;
  }, [watch]);

  return (
    <>
      <div className="content-wrapper">
        <h1 className="border-bottom pb-2 mt-4 mb-5">
          {" "}
          {isEditForm ? "Edycja pozycji wniosku " : "Nowa pozycja wniosku"}
        </h1>
        <LoadingContentWrapper isLoading={isLoading}>
          {!isLoading && (
            <FormProvider {...methods}>
              <form noValidate>

                <div className="row">
                  <div className="col-sm-8 offset-sm-4">
                    <h3>1. Wybór pozycji</h3>
                  </div>
                </div>

                <BusinessDocumentItemSelectorV2Fragment
                  name="documentItem"
                  control={control}
                  required={true}
                  ref={businessDocumentItemSelectorRef}
                  businessDocumentType={businessDocumentType.goodsIssue}
                  documentNumber={props.documentNumber}
                />

                <div className="row">
                  <div className="col-sm-8 offset-sm-4">
                    <h3>2. Dane zwrotu</h3>
                  </div>
                </div>

                <DecimalInputFormRow
                  {...getDecimalMetadata<ProductReturnRequestItemViewModel>(
                    "amount",
                    metadata["amount"],
                  )}
                  label="Ilość do zwrotu *"
                  longDescription="Ilość towaru do zwrotu (wymagane)."
                  control={control}
                  inlineDescription={undefined}
                  rules={{
                    validate: {
                      lessThanOrEqualToAmountInLine: (value) => {
                        let r: string | undefined = undefined;
                        const documentItem: businessDocumentItemVM | null =
                          getValues("documentItem");
                        if (
                          documentItem !== null &&
                          value !== null &&
                          typeof value === "number"
                        ) {
                          if (value > documentItem.amount) {
                            r = `Ilość do zwrotu nie może być większa niż ilość na pozycji dokumentu, tj. ${documentItem.amount}.`;
                          }
                        }
                        return r;
                      },
                    },
                  }}
                ></DecimalInputFormRow>

                <hr />
                <TextAreaSection
                  {...getTextAreaMetadata<ProductReturnRequestItemViewModel>(
                    "comments",
                    metadata["comments"],
                  )}
                  rows={6}
                  label="Uwagi"
                  longDescription="Uwagi do zwracanego towaru"
                  control={control}
                  inlineDescription={undefined}
                  required={false}
                />

                <div className="row pt-2">
                  <div className="offset-sm-4 col-sm-8">
                    <FormButtons
                      onSave={(e) => handleSubmit(onSubmit)(e)}
                      onCancel={() => props.onCancel()}
                      isSubmitting={isSubmitting}
                    ></FormButtons>
                  </div>
                </div>
              </form>
            </FormProvider>
          )}
        </LoadingContentWrapper>
      </div>
    </>
  );
}
